import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injectable, EventEmitter } from '@angular/core';
import { notSendToken } from '../interceptors/refresh-token.interceptor';
import { AppStateCart } from '../state/cart/cart.reducer';
import { Store } from '@ngrx/store';
import * as cart from '../state/cart/cart.actions';
import * as user from '../state/user/user.actions';
import { AppStateUser } from '../state/user/user.reducer';
import { getAuth, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
import { TGetCostumerDataResponse } from '@models/user.model';
import { lastValueFrom } from 'rxjs';
import { IDopplerRequest } from '@models/doppler-request.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private URL: string = environment.rootUrl;
  public avatar$ = new EventEmitter<boolean>();
  public refreshAuth$ = new EventEmitter<boolean>();

  constructor(
    private http: HttpClient,
    private store: Store<AppStateCart>,
    private storeUser: Store<AppStateUser>
  ) { }

  public signin(data: any): Promise<any> {
    return this.http.post(`${this.URL}/auth/login`, data, {
      context: notSendToken()
    }).toPromise();
  }

  public signup(data: any): Promise<any> {
    return this.http.post(`${this.URL}/users`, data, {
      context: notSendToken()
    }).toPromise();
  }

  public async signout(): Promise<any> {
    try {
      localStorage.clear();
      this.store.dispatch(cart.unSetCart());
      this.storeUser.dispatch(user.unSetUser());
    } catch (error) {
      return error;
    }
  }

  public getCurrentUser(): Promise<TGetCostumerDataResponse> {
    return lastValueFrom(this.http.get<TGetCostumerDataResponse>(`${this.URL}/users/user-data`));
  }


  public verifyCodeClient(id: any, data: any): Promise<any> {
    try {
      return this.http.post(`${this.URL}/twilio/verify-code/${id}/client`, data).toPromise();
    } catch (error: any) {
      return error;
    }
  }

  public sendEmailRestorePassword(data: object): Promise<any> {
    return this.http.post(`${this.URL}/auth/forgot-password`, data).toPromise();
  }

  public refreshToken(refreshToken: any): any {
    return this.http.post(`${this.URL}/auth/refresh-token`, refreshToken, {
      context: notSendToken()
    });
  }

  public updateProfile(data: object): Promise<any> {
    try {
      return this.http.put(`${this.URL}/users`, data).toPromise();
    } catch (error: any) {
      return error;
    }
  }

  public updatePassword(token: any, data: any): Promise<any> {
    return this.http.put(`${this.URL}/auth/update-password`, data).toPromise();
  }

  public restorePassword(token: any, email: string, data: any): Promise<any> {
    return this.http.post(`${this.URL}/auth/new-password?token=${token}&email=${email}`, data).toPromise();
  }

  public async onOpenFacebookPopup(): Promise<string | undefined> {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    const auth = getAuth();
    const result = await signInWithPopup(auth, provider);
    return FacebookAuthProvider.credentialFromResult(result)?.accessToken;
  }

  public async signinGoogle(data: any): Promise<any> {
    return this.http.post(`${this.URL}/auth/login-social/google`, data).toPromise();
  }

  public async signinFacebook(data: any): Promise<any> {
    return this.http.post(`${this.URL}/auth/login-social/facebook`, data).toPromise();
  }

  public async subscribeNewsLetter(dopplerRequest: IDopplerRequest): Promise<any> {
    return this.http.post(`${this.URL}/news-letter/suscribe`, dopplerRequest).toPromise();
  }

  public async resendVerificationCode(phone: string, user_id: string): Promise<any> {
    return this.http.post(`${this.URL}/twilio/send-code/${user_id}/client`, { cellphone: phone }).toPromise();
  }

  public async getPageStatus(): Promise<any> {
    try {
      return await this.http.get(`${this.URL}/page-status`).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async validatePassword(password: string): Promise<any> {
    try {
      return await this.http.get(`${this.URL}/auth/validate-password?password=${password}`).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async sendPasswordEnableSite(password: string): Promise<any> {
    try {
      return await this.http.post(`${this.URL}/page-status`, { password }).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async deleteAccount(): Promise<any> {
    try {
      return await this.http.delete(`${this.URL}/users`).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async deleteAccounMotive(motive: string): Promise<any> {
    try {
      return await this.http.patch(`${this.URL}/auth/delete-account`, { motive: motive }).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async verifyReCaptcha(token: string): Promise<any> {
    try {
      return await this.http.post(`${this.URL}/auth/verify-recaptcha`, { token }).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async updateCellphone(cellphone: string): Promise<any> {
    try {
      return await this.http.patch(`${this.URL}/users/update-cellphone`, { cellphone }).toPromise();
    } catch (error) {
      return error;
    }
  }

  public async verifyCodeAndUpdateCellphone(cellphone: string, code: string, id: any): Promise<any> {
    try {
      return await this.http.post(`${this.URL}/twilio/verify-code/${id}/client/update-cellphone`, { cellphone, code, id }).toPromise();
    } catch (error) {
      return error;
    }
  }

}
